import {
  Autocomplete,
  Box,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PartnerPlaceholderImage } from 'assets/img';
import defaultPartnerImage from 'assets/img/partners/default-partner-image.png';
import { Loader } from 'components/Forms';
import { useIsOverflow } from 'components/hooks';
import { FlexBox } from 'components/Structure';
import { ThemeTypography } from 'designSystem';
import Image from 'designSystem/DataDisplay/Image/Image';
import PaperWithAddItem from 'designSystem/Inputs/Paper/Paper';
import Icon from 'designSystem/Primitives/Icon/Icon';
import useSites from 'hooks/useSites';
import debounce from 'lodash/debounce';
import React, { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { ISite, ESiteType, SiteViewOption } from 'types/site.types';
import { SiteTypeLabels } from 'utils/site.utils';
import AddEditSiteClusterDialog from '../AddEditSiteClusterDialog';
import AddEditSiteDialog from '../AddEditSiteDialog';

interface ISitesAutocompleteProps {
  defaultValue?: ISite;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fontSize?: number;
  hasError?: boolean;
  errorMessage?: string;
  onSiteSelect: (site: ISite | undefined) => void;
}

const StyledAutocomplete = styled(Autocomplete<ISite>)<{ fontSize?: number }>(({ fontSize }) => ({
  '& .MuiAutocomplete-inputRoot': {
    paddingLeft: '16px !important',
    fontSize: fontSize ? fontSize : 12,
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
    padding: 4,
    '& .MuiInputBase-input': {
      fontSize: fontSize ? fontSize : 12,
    },
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]':
    {
      padding: 4,
      '& .MuiInputBase-input': {
        fontSize: fontSize ? fontSize : 12,
      },
    },
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  '&.MuiFormHelperText-root': {
    marginTop: -2,
  },
}));

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const TextFieldWithOutInput = styled(TextField)({
  '& .MuiInputBase-root': {
    cursor: 'pointer',
  },
  '& .MuiInputBase-input': {
    display: 'none',
  },
});

interface ISiteLitItemContent {
  site: ISite;
}

const SiteLitItemContent: FC<ISiteLitItemContent> = ({ site }) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasOverflow = useIsOverflow(ref);

  const country = site.coordinateFeatures.find(
    ({ featureType }) => featureType === 'COUNTRY'
  )?.locationName;

  return (
    <FlexBox
      ref={ref}
      height={30}
      gap={1}
      display="flex"
      alignItems="center"
      maxWidth="calc(100% - 28px)"
    >
      <Icon
        name={site.siteType === ESiteType.CLUSTER ? 'site-cluster-placeholder' : 'site-placeholder'}
        color="blue-ice"
        size="large"
        mr={1}
      />

      {site.siteType === ESiteType.CLUSTER && (
        <FlexBox gap={1} maxWidth={hasOverflow ? '20%' : undefined}>
          <Icon name="farm" size="large" color="black" />
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="BLACK" autoOverflow>
            {site.sites.count}{' '}
            {site.sitesType ? SiteTypeLabels[site.sitesType]?.toLowerCase() : 'site'}
            {site.sites.count === 1 ? '' : 's'}
          </ThemeTypography>
        </FlexBox>
      )}

      <FlexBox
        gap={0.5}
        maxWidth={
          hasOverflow
            ? `${40 + (site.siteType === ESiteType.CLUSTER ? 0 : 20) + (site.ownedBy ? 0 : 40)}%`
            : undefined
        }
      >
        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
          {site.title}:
        </ThemeTypography>
        <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
          {SiteTypeLabels[site.siteType]}
          {country ? ` (${country})` : ''}
        </ThemeTypography>
      </FlexBox>

      {site.ownedBy && (
        <FlexBox gap={1} maxWidth={hasOverflow ? '40%' : undefined}>
          {site.ownedBy.logo ? (
            <Image
              width={20}
              height={20}
              image={site.ownedBy.logo}
              backupImageUrl={PartnerPlaceholderImage}
            />
          ) : (
            <Icon name="partner" size="large" color="blue-ice" />
          )}
          <ThemeTypography variant="BODY_MEDIUM" color="GRAY_80" autoOverflow>
            {site.ownedBy?.name}
          </ThemeTypography>
        </FlexBox>
      )}
    </FlexBox>
  );
};

const SitesAutocomplete: FC<ISitesAutocompleteProps> = ({
  defaultValue,
  placeholder = 'Search and select site',
  size = 'medium',
  disabled,
  fontSize,
  hasError,
  errorMessage = 'Please select a site',
  onSiteSelect,
}) => {
  const [createSiteClusterDialogOpen, setCreateSiteClusterDialogOpen] = useState(false);
  const [createSiteDialogOpen, setCreateSiteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<ISite | null>(defaultValue || null);
  const sizeImage = size === 'small' ? 14 : 17;
  const { sites, loading, refetch, searchTerm, handleSearchTermChange } = useSites({
    defaultViewOption: SiteViewOption.CLUSTER,
    skipUseQueryParams: true,
  });

  /**
   * We have to make sure the defaultSelected site is always included in the list of sites to be displayed
   */
  const sitesWithSelectedOption = useMemo(() => {
    if (selectedSite && !sites.find(site => site.id === selectedSite.id)) {
      return [selectedSite, ...sites];
    }
    return sites;
  }, [sites, selectedSite]);

  const handleSelectSite = (event: ChangeEvent<unknown>, site: ISite | null) => {
    setSelectedSite(site);
    onSiteSelect(site || undefined);
  };

  const handleNewSiteCreated = async (site: ISite) => {
    setSelectedSite(site);
    onSiteSelect(site);
    await refetch();
  };

  const handleInputChange = debounce(handleSearchTermChange, 500);

  return (
    <FormControl fullWidth size={size} error={hasError}>
      <StyledAutocomplete
        loading={loading}
        loadingText={
          <LoadingContainer>
            <Loader size={20} />
          </LoadingContainer>
        }
        value={selectedSite}
        options={sitesWithSelectedOption}
        onChange={handleSelectSite}
        filterOptions={options => options}
        inputValue={searchTerm || undefined}
        onInputChange={(_, value) => handleInputChange(value)}
        fullWidth
        selectOnFocus
        fontSize={fontSize}
        size={size}
        disabled={disabled}
        PaperComponent={({ children }) => (
          <PaperWithAddItem
            data-testId="create-sites-autocomplete"
            renderBottomItem={() => (
              <>
                <MenuItem onMouseDown={() => setCreateSiteDialogOpen(true)}>
                  <Icon name="plus" mr={1} />
                  Add new site
                </MenuItem>
                <MenuItem onMouseDown={() => setCreateSiteClusterDialogOpen(true)}>
                  <Icon name="plus" mr={1} />
                  Add new origin cluster
                </MenuItem>
              </>
            )}
          >
            {children}
          </PaperWithAddItem>
        )}
        getOptionLabel={option => option.title}
        renderOption={(props, option: ISite, state) => {
          return (
            <li {...props} {...state} key={option.id}>
              <SiteLitItemContent site={option} />
            </li>
          );
        }}
        renderInput={params => {
          // Hacked the input to be empty to show the actually selected site according to design
          // TODO: we have to build our own autocomplete component to avoid this hack
          if (selectedSite) {
            const modifiedParams = { ...params, inputProps: { ...params.inputProps, value: '' } };
            return (
              <ClickAwayListener
                onClickAway={event =>
                  modifiedParams.inputProps.onBlur?.(
                    event as unknown as React.FocusEvent<HTMLInputElement>
                  )
                }
              >
                <TextFieldWithOutInput
                  {...modifiedParams}
                  error={hasError}
                  variant="outlined"
                  onKeyPress={keyEvent => {
                    // Prevents the form from being submitted on enter.
                    if (keyEvent.key === 'Enter') {
                      keyEvent.preventDefault();
                    }
                  }}
                  onMouseDown={modifiedParams.inputProps.onMouseDown}
                  InputProps={{
                    style: { height: size === 'medium' ? 40 : 30, userSelect: 'none' },
                    ...modifiedParams.InputProps,
                    // @ts-ignore
                    'data-testId': 'sites-autocomplete-input',
                    startAdornment: <SiteLitItemContent site={selectedSite} />,
                  }}
                />
              </ClickAwayListener>
            );
          }

          return (
            <TextField
              {...params}
              error={hasError}
              variant="outlined"
              placeholder={placeholder}
              onKeyPress={keyEvent => {
                // Prevents the form from being submitted on enter.
                if (keyEvent.key === 'Enter') {
                  keyEvent.preventDefault();
                }
              }}
              InputProps={{
                style: { height: size === 'medium' ? 40 : 30 },
                ...params.InputProps,
                // @ts-ignore
                'data-testId': 'sites-autocomplete-input',
                startAdornment: (
                  <Image
                    width={sizeImage}
                    height={sizeImage}
                    backupImageUrl={defaultPartnerImage}
                    borderRadius={0}
                  />
                ),
              }}
            />
          );
        }}
      />
      {hasError && <StyledFormHelperText>{errorMessage}</StyledFormHelperText>}

      {/* Workaround: This dialog needs to live here and NOT in the context since the callback is changing the state */}
      <AddEditSiteDialog
        open={createSiteDialogOpen}
        onSiteCreated={handleNewSiteCreated}
        onClose={() => setCreateSiteDialogOpen(false)}
      />
      {/* Workaround: This dialog needs to live here and NOT in the context since the callback is changing the state */}
      <AddEditSiteClusterDialog
        open={createSiteClusterDialogOpen}
        onSiteClusterCreated={handleNewSiteCreated}
        onClose={() => setCreateSiteClusterDialogOpen(false)}
      />
    </FormControl>
  );
};

export default SitesAutocomplete;
